import { AsYouType } from 'libphonenumber-js'

export interface PhoneMaskConfig {
  mask: string
  length: number
  name: string
}

export const PHONE_MASKS: Record<string, PhoneMaskConfig> = {
  '+380': { mask: '+380 (##) ###-##-##', length: 12, name: 'Ukraine' }, // 380 + 9 digits = 12 ✓
  '+1': { mask: '+1 (###) ###-####', length: 11, name: 'USA/Canada' }, // 1 + 10 digits = 11 ✓
  '+44': { mask: '+44 ## #### ####', length: 13, name: 'UK' }, // 44 + 11 digits = 13 ✓
  '+49': { mask: '+49 ### ########', length: 13, name: 'Germany' }, // 49 + 11 digits = 13 ✓
  '+33': { mask: '+33 # ## ## ## ##', length: 12, name: 'France' }, // 33 + 10 digits = 12 ✓
  '+39': { mask: '+39 ### ### ####', length: 12, name: 'Italy' }, // 39 + 10 digits = 12 ✓
  '+34': { mask: '+34 ### ### ###', length: 11, name: 'Spain' }, // 34 + 9 digits = 11 ✓
  '+48': { mask: '+48 ### ### ###', length: 11, name: 'Poland' }, // 48 + 9 digits = 11 ✓
  '+31': { mask: '+31 # ########', length: 11, name: 'Netherlands' }, // 31 + 9 digits = 11 ✓
  '+32': { mask: '+32 # ## ## ## ##', length: 12, name: 'Belgium' }, // 32 + 10 digits = 12 ✓
  '+43': { mask: '+43 # #### ####', length: 11, name: 'Austria' }, // 43 + 9 digits = 11 ✓
  '+41': { mask: '+41 ## ### ## ##', length: 12, name: 'Switzerland' }, // 41 + 10 digits = 12 ✓
  '+46': { mask: '+46 ## ### ## ##', length: 12, name: 'Sweden' }, // 46 + 10 digits = 12 ✓
  '+47': { mask: '+47 ### ## ###', length: 10, name: 'Norway' }, // 47 + 8 digits = 10 ✓
  '+45': { mask: '+45 ## ## ## ##', length: 10, name: 'Denmark' }, // 45 + 8 digits = 10 ✓
  '+358': { mask: '+358 ## ### ####', length: 12, name: 'Finland' }, // 358 + 9 digits = 12 ✓
}

// E.164 allows up to 15 digits total; used as a permissive cap for countries
// that are not in the curated PHONE_MASKS list (free international input).
export const INTERNATIONAL_MAX_DIGITS = 15

const REGION_DISPLAY = (() => {
  try {
    return new (Intl as any).DisplayNames(['en'], { type: 'region' })
  } catch {
    return null
  }
})()

const regionName = (isoCountry?: string): string | null => {
  if (!isoCountry || !REGION_DISPLAY) {
    return null
  }
  try {
    return REGION_DISPLAY.of(isoCountry) ?? null
  } catch {
    return null
  }
}

// Returns the matching config, or null when the country code is NOT in the
// curated list. Unlike detectCountryMask it does NOT fall back to Ukraine, so
// callers can tell "known country" from "unknown / international".

export const detectCountryMaskOrNull = (value: string): PhoneMaskConfig | null => {
  if (!value) {
    return null
  }

  const formatter = new AsYouType()
  formatter.input(value.startsWith('+') ? value : `+${value}`)
  const callingCode = formatter.getCallingCode()
  if (!callingCode) {
    return null
  }

  return PHONE_MASKS[`+${callingCode}`] ?? null
}

export const detectCountryMask = (value: string): PhoneMaskConfig => {
  const known = detectCountryMaskOrNull(value)
  if (known) {
    return known
  }

  if (value) {
    const formatter = new AsYouType()
    formatter.input(value.startsWith('+') ? value : `+${value}`)
    const callingCode = formatter.getCallingCode()
    if (callingCode) {
      const name = regionName(formatter.getCountry()) ?? `+${callingCode}`
      return { mask: '', length: INTERNATIONAL_MAX_DIGITS, name }
    }
  }
  return PHONE_MASKS['+380']
}

export const getCountryByCode = (countryCode: string): PhoneMaskConfig | null => {
  return PHONE_MASKS[countryCode] || null
}

export const getAllCountries = (): Array<{ code: string; config: PhoneMaskConfig }> => {
  return Object.entries(PHONE_MASKS).map(([code, config]) => ({
    code,
    config
  }))
}